import { patchState, signalStore, withComputed, withMethods } from '@ngrx/signals';
import { removeEntities, removeEntity, setEntities, setEntity, withEntities } from '@ngrx/signals/entities';
import { IrisDMSTag, IrisDMSTagI } from '../models/IrisDMSTag';
import { setError, setLoaded, setLoading, withRequestState } from '@iris/common/signals/features/request-status.feature';
import { Signal, computed, inject } from '@angular/core';
import { IrisTagsService } from '@iris/common/services/tags/tags.service';
import { Observable, catchError, filter, lastValueFrom, map, of, pipe, switchMap } from 'rxjs';
import { IrisGlobalSandbox } from '@iris/common/redux/global.sandbox';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { tapResponse } from '@ngrx/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { sortBy } from 'lodash';

export type IrisDmsTagType = 'Public' | 'Project';

export const IrisDmsTagsStore = signalStore(
  { providedIn: 'root', protectedState: false },
  withEntities<IrisDMSTagI>(),
  withRequestState<IrisDmsTagType>({ collection: 'Public' }),
  withRequestState<IrisDmsTagType>({ collection: 'Project' }),
  withRequestState(),
  withComputed((store, globalSandbox = inject(IrisGlobalSandbox)) => ({
    publicTags: computed(() => sortBy(store.entities().filter(({ projectId }) => !projectId), tag => tag.name.toLowerCase())),
    allTags: store.entities,
    activeTags: computed(() => store.entities().filter(({ isActive }) => isActive)),
    projectId: globalSandbox.currentProjectId,
  })),
  withComputed(store => ({
    hasPublicTags: computed(() => !!store.publicTags().length),
    getDmsTags: computed(() => {
      const projectTags = sortBy(store.entities().filter(tag => !!tag.projectId && tag.projectId === store.projectId()), tag => tag.name.toLowerCase());
      const publicTags = store.publicTags();
      return [...projectTags, ...publicTags];
    }),
  })),
  withMethods((
    store,
    tagsService = inject(IrisTagsService),
  ) => ({
    getTagById: (tagId: number): IrisDMSTagI => new IrisDMSTag(store.entityMap()[tagId]),
    getTagsByIds: (tagsIds: number[]): IrisDMSTagI[] => tagsIds.reduce<IrisDMSTagI[]>((result, tagId) => {
      const tag = store.entityMap()[tagId];
      if (tag) {
        result.push(tag);
      }
      return result;
    }, []),
    getProjectTags(projectId: number): Signal<IrisDMSTagI[]> {
      return computed(() => {
        if (!projectId) {
          return [];
        }
        return sortBy(store.entities().filter(tag => !!tag.projectId && tag.projectId === projectId), tag => tag.name.toLowerCase());
      });
    },
    getPublicAndProjectTags(projectId: number): Signal<IrisDMSTagI[]> {
      return computed(() => sortBy(store.entities().filter(tag => !tag.projectId || tag.projectId === projectId), tag => tag.name.toLowerCase()));
    },
    fetchProjectTags: rxMethod<{ projectId: number; calcualteFileCount?: boolean }>(
      pipe(
        filter(() => !store.ProjectLoading()),
        switchMap(({ projectId, calcualteFileCount }) => {
          patchState(store, setLoading<IrisDmsTagType>('Project'));
          return tagsService.getDmsProjectTags(projectId, calcualteFileCount).pipe(
            tapResponse({
              next: projectTags => {
                patchState(store, setEntities(projectTags));
                patchState(store, setLoaded<IrisDmsTagType>('Project'));
                if (store.ProjectLoaded() && store.PublicLoaded()) {
                  patchState(store, setLoaded());
                }
              },
              error: (err: HttpErrorResponse) => patchState(store, setError<IrisDmsTagType>(err.message, 'Project')),
            }),
          );
        }),
      ),
    ),
    fetchPublicTags: rxMethod<boolean>(
      pipe(
        filter(() => !store.PublicLoading()),
        switchMap((calcualteFileCount = false) => {
          patchState(store, setLoading<IrisDmsTagType>('Public'));
          return tagsService.getDmsPublicTags(calcualteFileCount).pipe(
            tapResponse({
              next: publicTags => {
                patchState(store, setEntities(publicTags));
                patchState(store, setLoaded<IrisDmsTagType>('Public'));
                if ((store.ProjectLoaded() || !store.projectId()) && store.PublicLoaded()) {
                  patchState(store, setLoaded());
                }
              },
              error: (err: HttpErrorResponse) => patchState(store, setError<IrisDmsTagType>(err.message, 'Public')),
            }),
          );
        }),
      ),
    ),
    createTag: rxMethod<IrisDMSTagI>(
      pipe(
        switchMap(tag => tagsService.add(tag).pipe(
          tapResponse({
            next: createdTag => {
              patchState(store, setEntity(createdTag));
            },
            error: (err: HttpErrorResponse) => patchState(store, setError(err.message)),
          })),
        )),
    ),
    updateTag: rxMethod<IrisDMSTagI>(
      pipe(
        switchMap(tag => tagsService.update(tag.id, tag).pipe(
          tapResponse({
            next: updatedTag => {
              patchState(store, setEntity(updatedTag));
            },
            error: (err: HttpErrorResponse) => patchState(store, setError(err.message)),
          })),
        )),
    ),
    async removeTag(tagId: number): Promise<void> {
      await lastValueFrom(tagsService.delete(tagId));
      patchState(store, removeEntity(tagId));
    },
    removeTagsFromStoreByName(name: string, isProject = false): void {
      patchState(store, removeEntities(entity => entity.name === name && (!isProject || !!entity.projectId)));
    },
    checkTagExistence$(tagName: string, projectId: number): Observable<boolean>{
      const checkRequest = projectId
        ? tagsService.getProjectTagByName(projectId, tagName)
        : tagsService.getPublicTagByName(tagName);

      return checkRequest.pipe(
        map(tag => !!tag),
        catchError(() => of(false)),
      );
    },
  })),
  withMethods(store => ({
    getActivePublicAndProjectTags(projectId: number): Signal<IrisDMSTagI[]> {
      return computed(() => store.getPublicAndProjectTags(projectId)().filter(tag => tag.isActive));
    },
  })),
);
